// store/modules/auth.js
import { exchangeCodeForToken, refreshTokenApi } from '@/api/auth'
import { setTokens, removeTokens } from '@/utils/auth'
import Cookies from 'js-cookie'
import store from '@/store'

// token刷新定时器
let refreshTimer = null

/**
 * 启动token自动刷新
 * @param {string} refreshToken 刷新token
 */
function startTokenRefresh(refreshToken) {
  // 清除现有的定时器
  if (refreshTimer) {
    clearInterval(refreshTimer)
  }
  
  console.log('启动token自动刷新定时器，每10分钟刷新一次')
  
  // 每10分钟刷新一次token
  refreshTimer = setInterval(async () => {
    try {
      console.log('定时器触发，开始刷新token...')
      const response = await refreshTokenApi({token: refreshToken})
      if (response.code === 200 && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data
        setTokens(accessToken, newRefreshToken)
        // 更新store中的token - 通过localStorage和Cookies同步
        Cookies.set('access_token', accessToken)
        Cookies.set('refresh_token', newRefreshToken)
        console.log('token刷新成功')
      }
    } catch (error) {
      console.error('Token刷新失败:', error)
      // 刷新失败时清除定时器
      if (refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
      }
    }
  }, 10 * 60 * 1000) // 10分钟
}


const state = {
  accessToken: Cookies.get('access_token') || null,
  refreshToken: Cookies.get('refresh_token') || null,
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  isLoading: false
}

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },
  SET_TOKENS(state, { accessToken, refreshToken }) {
    state.accessToken = accessToken
    state.refreshToken = refreshToken
  },
  SET_USER(state, user) {
    state.user = user
  },
  CLEAR_AUTH(state) {
    state.accessToken = null
    state.refreshToken = null
    state.user = null
  }
}

const actions = {
  // 生成state参数
  generateState() {
    const stateParam = Math.random().toString(36).substring(2) + Date.now().toString(36)
    localStorage.setItem('oauth_state', stateParam)
    return stateParam
  },

  // 验证state参数
  validateState(receivedState) {
    const storedState = localStorage.getItem('oauth_state')
    localStorage.removeItem('oauth_state')
    return storedState === receivedState
  },

  // 处理认证回调
  async handleCallback({ commit, dispatch }, { code, state }) {
    commit('SET_LOADING', true)
    
    try {
      // 验证state参数
      if (!dispatch('validateState', state)) {
        throw new Error('Invalid state parameter')
      }

      // 使用code换取token
      const response = await exchangeCodeForToken({ 
        code: code,
        clientSecret: process.env.VUE_APP_CLIENT_SECRET
      })
      
      if (response.code === 200 && response.data) {
        const { accessToken, refreshToken, userInfo } = response.data
        
        // 设置token
        commit('SET_TOKENS', {
          accessToken: accessToken,
          refreshToken: refreshToken
        })
        setTokens(accessToken, refreshToken)

        // 设置用户信息
        commit('SET_USER', userInfo)
        localStorage.setItem('user', JSON.stringify(userInfo))

        // 同步用户信息到user模块
        await store.dispatch('user/login', {
          token: accessToken,
          userInfo: userInfo
        })

        // 启动token自动刷新
        startTokenRefresh(refreshToken)

        return userInfo
      } else {
        throw new Error(response.message || '认证失败')
      }
    } catch (error) {
      commit('CLEAR_AUTH')
      removeTokens()
      localStorage.removeItem('user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // 刷新token
  async refreshToken({ commit, state }) {
    if (!state.refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await refreshTokenApi({token: state.refreshToken})
      
      if (response.code === 200 && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data
        
        commit('SET_TOKENS', {
          accessToken: accessToken,
          refreshToken: newRefreshToken
        })
        setTokens(accessToken, newRefreshToken)

        return accessToken
      } else {
        throw new Error(response.message || 'Token刷新失败')
      }
    } catch (error) {
      commit('CLEAR_AUTH')
      removeTokens()
      localStorage.removeItem('user')
      throw error
    }
  },

  // 登出
  logout({ commit }) {
    commit('CLEAR_AUTH')
    removeTokens()
    localStorage.removeItem('user')
    localStorage.removeItem('oauth_state')
  }
}

const getters = {
  isAuthenticated: state => !!state.accessToken,
  user: state => state.user,
  isLoading: state => state.isLoading,
  accessToken: state => state.accessToken
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
